import ExcelJS from 'exceljs';

// Warna standar dipakai di semua export Excel & PDF di aplikasi ini, biar konsisten.
export const EXPORT_COLORS = {
  headerFill: 'FF3F51B5', // indigo, senada dengan warna utama aplikasi
  headerFillPdf: [63, 81, 181],
  headerFontColor: 'FFFFFFFF',
  subtotalFill: 'FFE8EAF6',
  stripeFill: 'FFF5F5F5',
  grandTotalFill: 'FFFFD54F', // kuning/gold, beda dari subtotal per kategori
  border: { style: 'thin', color: { argb: 'FFBDBDBD' } },
  // Padanan RGB dari warna di atas, dipakai untuk styling cell jspdf-autotable
  subtotalFillPdf: [232, 234, 246],
  grandTotalFillPdf: [255, 213, 79],
};

// Preset opsi untuk jspdf-autotable supaya semua tabel PDF punya header berwarna,
// baris selang-seling, dan padding yang konsisten.
export const pdfTableTheme = {
  headStyles: {
    fillColor: EXPORT_COLORS.headerFillPdf,
    textColor: [255, 255, 255],
    fontStyle: 'bold',
  },
  alternateRowStyles: { fillColor: [245, 245, 245] },
  styles: { fontSize: 9, cellPadding: 3 },
};

/**
 * Membuat & mengunduh file Excel (.xlsx) dengan header berwarna, border, baris
 * selang-seling, teks center, dan opsional baris subtotal yang ditandai tebal
 * dengan label yang di-merge (supaya tidak kepotong seperti "Total Kat...").
 *
 * @param {Object} options
 * @param {string} options.fileName
 * @param {string} [options.sheetName]
 * @param {string} [options.title] - judul di baris pertama, di-merge sepanjang tabel
 * @param {Array<[string, string]>} [options.metaRows] - pasangan label/nilai di atas tabel (mis. Outlet, Tanggal); nilainya di-merge dari kolom 3 sampai akhir
 * @param {string[]} options.headers
 * @param {Array<Array<string|number>>} options.rows
 * @param {number[]} [options.columnWidths]
 * @param {number[]} [options.subtotalRowIndexes] - index baris (dalam `rows`) yang ditandai sebagai subtotal
 * @param {number} [options.subtotalLabelSpan] - jumlah kolom di kiri yang di-merge jadi satu sel label pada baris subtotal (misal 2 supaya "Total Kategori Ayam" tidak kepotong). Default 1 (tidak merge).
 * @param {Object} [options.grandTotalRow] - baris total keseluruhan di baris paling akhir, disorot kuning.
 * @param {string} [options.grandTotalRow.label] - teks label, mis. "GRAND TOTAL"
 * @param {Array<string|number>} options.grandTotalRow.values - nilai untuk kolom-kolom terakhir (jumlahnya menentukan berapa kolom numerik di kanan)
 */
export async function exportStyledExcel({
  fileName,
  sheetName = 'Sheet1',
  title,
  metaRows = [],
  headers,
  rows,
  columnWidths,
  subtotalRowIndexes = [],
  subtotalLabelSpan = 1,
  grandTotalRow,
}) {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(sheetName);

  let currentRow = 1;

  if (title) {
    sheet.mergeCells(currentRow, 1, currentRow, headers.length);
    const titleCell = sheet.getCell(currentRow, 1);
    titleCell.value = title;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center' };
    currentRow += 2;
  }

  metaRows.forEach(([label, value]) => {
    sheet.getCell(currentRow, 1).value = label;
    sheet.getCell(currentRow, 1).font = { bold: true };
    if (headers.length > 2) {
      sheet.mergeCells(currentRow, 2, currentRow, headers.length);
    }
    const valueCell = sheet.getCell(currentRow, headers.length > 1 ? 2 : 1);
    valueCell.value = value;
    valueCell.alignment = { horizontal: 'left' };
    currentRow += 1;
  });
  if (metaRows.length) currentRow += 1;

  const headerRowIndex = currentRow;
  headers.forEach((h, i) => {
    const cell = sheet.getCell(headerRowIndex, i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: EXPORT_COLORS.headerFontColor } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.headerFill } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: EXPORT_COLORS.border,
      bottom: EXPORT_COLORS.border,
      left: EXPORT_COLORS.border,
      right: EXPORT_COLORS.border,
    };
  });
  currentRow += 1;

  rows.forEach((row, rIdx) => {
    const isSubtotal = subtotalRowIndexes.includes(rIdx);
    const border = {
      top: EXPORT_COLORS.border,
      bottom: EXPORT_COLORS.border,
      left: EXPORT_COLORS.border,
      right: EXPORT_COLORS.border,
    };
    const rowFill = isSubtotal
      ? { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.subtotalFill } }
      : rIdx % 2 === 1
        ? { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.stripeFill } }
        : null;

    if (isSubtotal && subtotalLabelSpan > 1) {
      sheet.mergeCells(currentRow, 1, currentRow, subtotalLabelSpan);
      const labelCell = sheet.getCell(currentRow, 1);
      labelCell.value = row[0];
      labelCell.font = { bold: true };
      labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      labelCell.border = border;
      if (rowFill) labelCell.fill = rowFill;

      for (let cIdx = subtotalLabelSpan; cIdx < row.length; cIdx++) {
        const cell = sheet.getCell(currentRow, cIdx + 1);
        cell.value = row[cIdx];
        if (typeof row[cIdx] === 'number') cell.numFmt = '#,##0';
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = border;
        if (rowFill) cell.fill = rowFill;
      }
    } else {
      row.forEach((val, cIdx) => {
        const cell = sheet.getCell(currentRow, cIdx + 1);
        cell.value = val;
        if (typeof val === 'number') cell.numFmt = '#,##0';
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = border;
        if (isSubtotal) cell.font = { bold: true };
        if (rowFill) cell.fill = rowFill;
      });
    }
    currentRow += 1;
  });

  if (grandTotalRow) {
    const { label, values } = grandTotalRow;
    const labelSpan = headers.length - values.length;
    const border = {
      top: { style: 'thin', color: { argb: 'FF9E9E00' } },
      bottom: { style: 'thin', color: { argb: 'FF9E9E00' } },
      left: { style: 'thin', color: { argb: 'FF9E9E00' } },
      right: { style: 'thin', color: { argb: 'FF9E9E00' } },
    };
    const fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: EXPORT_COLORS.grandTotalFill } };

    if (labelSpan > 1) {
      sheet.mergeCells(currentRow, 1, currentRow, labelSpan);
    }
    const labelCell = sheet.getCell(currentRow, 1);
    labelCell.value = label;
    labelCell.font = { bold: true, size: 12 };
    labelCell.alignment = { horizontal: 'center', vertical: 'middle' };
    labelCell.border = border;
    labelCell.fill = fill;
    for (let c = 2; c <= labelSpan; c++) {
      const cell = sheet.getCell(currentRow, c);
      cell.border = border;
      cell.fill = fill;
    }

    values.forEach((val, i) => {
      const cell = sheet.getCell(currentRow, labelSpan + i + 1);
      cell.value = val;
      if (typeof val === 'number') cell.numFmt = '#,##0';
      cell.font = { bold: true, size: 12 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = border;
      cell.fill = fill;
    });
    currentRow += 1;
  }

  if (columnWidths) {
    columnWidths.forEach((w, i) => {
      sheet.getColumn(i + 1).width = w;
    });
  } else {
    headers.forEach((h, i) => {
      sheet.getColumn(i + 1).width = Math.max(14, String(h).length + 4);
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
